import { useEffect } from 'react'

import { PlanType, ScoreStatus } from '$extensionSrc/__generated__/graphql'

import { useExtensionData } from '../../hooks/extensionStorageListener'
import { sendGetCurrentUser } from '../../utils/communication'
import { PLATFORM, STORAGE_KEY } from '../../utils/enums'

export type User = {
  autoscanSettings?: {
    enabled: boolean
    platform: PLATFORM
    platformUserId: string
  }[]
  email: string
  /**
   * Used in the old version of Goby to differentiate what level of access
   * the user should have to the different features; deprecated in 2.0.
   */
  hasActivePlan: boolean
  id: string
  name: string
  nextAutoscanDate?: Date
  planExpiresOn?: string
  planType: PlanType
  registeredOn: string
  trialExpiresOn?: string
  trialPlatform?: PLATFORM
  impactMetrics?: {
    overallScore: number | null
    overallStatus: ScoreStatus | null
  }
}

export async function setUser(user: User | null): Promise<void> {
  return chrome.storage.local.set({
    [STORAGE_KEY.CURRENT_USER]: user,
  })
}

// Does NOT refresh the logged in user. Only returns the current user id.
// Used to associate notifications with the currently logged in user.
export async function getCurrentUserId(): Promise<string | null> {
  const currentUser = await chrome.storage.local.get(STORAGE_KEY.CURRENT_USER)
  if (currentUser) {
    return (currentUser[STORAGE_KEY.CURRENT_USER] as User | undefined)?.id
  }

  return null
}

export async function updateCurrentUser(user: Partial<User>): Promise<void> {
  const currentUser = await chrome.storage.local.get(STORAGE_KEY.CURRENT_USER)
  const updatedUser = {
    ...(currentUser[STORAGE_KEY.CURRENT_USER] as User | undefined),
    ...user,
  }

  return chrome.storage.local.set({
    [STORAGE_KEY.CURRENT_USER]: updatedUser,
  })
}

export function useUser(): User | null | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.CURRENT_USER],
  })

  // Refresh local storage user from server user on mount
  useEffect(() => {
    const query = async () => {
      const response = await sendGetCurrentUser()
      if (response.error) {
        return
      }
      setUser(response.data)
    }

    query()
  }, [])

  if (!isLoaded || !data) {
    // Still loading
    return undefined
  }

  return data[STORAGE_KEY.CURRENT_USER] || null
}
